// askSmart-ui/src/pages/TOSP/tabs/UnitsTab.tsx
import React, { useState } from "react";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import InfoModal from "../../../components/InfoModal";
import UnitModal, { UnitFormValues } from "../components/UnitModal";
import UnitDetailsModal from "../components/UnitDetailsModal";
import { formatCurrency, formatSqFt } from "../format";
import { useTospData } from "../TospDataContext";
import {
  activeOccupancyOf,
  bmvaAnnualFee,
  bmvaMonthlyFee,
  bmvaRate,
  currentAllocation,
  TaxRollRecord,
  tenantLabel,
  Unit,
  newId,
} from "../types";
import { useGridSort } from "../useGridSort";

// Shared by the header and every row so the columns can't drift apart.
const GRID_COLUMNS = "1.4fr 0.7fr 1.4fr 1.1fr 0.9fr 0.9fr 1fr 1fr 1fr 1.4fr";

type SortCol =
  | "building"
  | "suiteNumber"
  | "tenant"
  | "spaceType"
  | "occupancySqFt"
  | "bmvaSqFt"
  | "bmvaRatePerSqFt"
  | "annualFee"
  | "monthlyFee";

const SORT_COLUMNS: { col: SortCol; label: string }[] = [
  { col: "building", label: "Building" },
  { col: "suiteNumber", label: "Suite #" },
  { col: "tenant", label: "Tenant" },
  { col: "spaceType", label: "Space Type" },
  { col: "occupancySqFt", label: "Occupancy SqFt" },
  { col: "bmvaSqFt", label: "BMVA SqFt" },
  { col: "bmvaRatePerSqFt", label: "BMVA Rate / SqFt" },
  { col: "annualFee", label: "BMVA Annual Fee" },
  { col: "monthlyFee", label: "BMVA Monthly Fee" },
];

// Units — the rentable spaces within a building. Tenants will reference these
// by id.
export default function UnitsTab() {
  const {
    buildings,
    units,
    saveUnit,
    deleteUnit,
    tenants,
    buildingName,
    taxRolls,
    saveTaxRolls,
    configuration,
  } = useTospData();
  // The unit being edited, `null` for a new one, `undefined` when closed — so
  // "add" and "edit" share one modal without a second open/closed flag.
  const [editing, setEditing] = useState<Unit | null | undefined>(undefined);
  const [viewing, setViewing] = useState<Unit | null>(null);
  const [deleting, setDeleting] = useState<Unit | null>(null);
  const [blockedDelete, setBlockedDelete] = useState<string | null>(null);

  /**
   * Who holds this unit, or "" when nobody does.
   *
   * activeOccupancyOf, so a lease whose term has lapsed still counts — the
   * tenant is in the premises until somebody ends the lease explicitly. See
   * isOccupancyActive.
   */
  const occupantName = (unitId: string): string => {
    const held = activeOccupancyOf(unitId, tenants);
    return held ? tenantLabel(held.tenant) : "";
  };

  const { sortButton, sortRows } = useGridSort<SortCol, Unit>("building", (unit, col) => {
    switch (col) {
      case "building":
        return buildingName(unit.buildingId);
      case "suiteNumber":
        return unit.suiteNumber;
      case "tenant":
        // Sorts the unassigned together at one end rather than scattering them
        // under whatever letter "None" would fall.
        return occupantName(unit.id) || "zzzz";
      case "spaceType":
        return unit.spaceType;
      case "occupancySqFt":
        return unit.occupancySqFt;
      case "bmvaSqFt":
        return unit.bmvaSqFt;
      case "bmvaRatePerSqFt":
        return bmvaRate(configuration, unit);
      case "annualFee":
        return bmvaAnnualFee(unit, configuration);
      case "monthlyFee":
        return bmvaMonthlyFee(unit, configuration);
      default:
        return "";
    }
  });

  const handleSave = async (values: UnitFormValues) => {
    // The form stages roll-part picks; the register is where they live. Split
    // them off so the unit document never carries a copy that could go stale.
    const { taxRollPartKeys, ...unitFields } = values;
    const unitId = editing?.id || newId("unit");
    const wanted = new Set(taxRollPartKeys);

    try {
      await saveUnit({ ...(editing || { id: unitId }), ...unitFields });

      // Only rolls whose parts actually changed hands are written — attaching
      // one part to a ten-roll unit costs one write, not ten. Earlier years'
      // allocations are never touched, so a settled year keeps its division.
      const changed: TaxRollRecord[] = [];
      for (const roll of taxRolls) {
        const current = currentAllocation(roll);
        let dirty = false;
        const parts = current.parts.map((part) => {
          const mine = part.assignedTo?.type === "unit" && part.assignedTo.id === unitId;
          const shouldBeMine = wanted.has(`${roll.id}:${part.id}`);
          if (mine === shouldBeMine) return part;
          dirty = true;
          return {
            ...part,
            assignedTo: shouldBeMine ? { type: "unit" as const, id: unitId } : null,
          };
        });
        if (dirty) {
          changed.push({
            ...roll,
            allocations: [...roll.allocations.slice(0, -1), { ...current, parts }],
          });
        }
      }
      if (changed.length > 0) await saveTaxRolls(changed);

      setEditing(undefined);
    } catch {
      /* surfaced by the provider */
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await deleteUnit(deleting.id);
      setDeleting(null);
    } catch {
      /* surfaced by the provider */
    }
  };

  // A unit that any lease has ever demised can't be deleted — removing it
  // would strand that leased space and lose the unit's lease history. The
  // space has to be unassigned from the lease first.
  const requestDelete = (unit: Unit) => {
    const occupants = tenants.filter((t) =>
      (t.occupancies || []).some((o) =>
        (o.leasedSpaces || []).some((space) => space.unitId === unit.id),
      ),
    );
    if (occupants.length > 0) {
      setBlockedDelete(
        `Suite ${unit.suiteNumber} can't be deleted because it is leased by ${occupants
          .map((t) => tenantLabel(t))
          .join(", ")}. Unassign it from the leased space on that tenant first.`,
      );
      return;
    }
    setDeleting(unit);
  };

  return (
    <section className="tosp-card">
      <div className="tosp-toolbar">
        <button
          className="tosp-btn-primary"
          onClick={() => setEditing(null)}
          disabled={buildings.length === 0}
          title={buildings.length === 0 ? "Add a building first" : undefined}
        >
          + Add Unit
        </button>
      </div>

      {/* The grid fits the 1500px card; the scroll wrapper only engages on a
          narrow viewport. */}
      <div className="tosp-grid-scroll">
        <div className="tosp-grid-body" style={{ minWidth: 1140 }}>
          <div className="tosp-grid-header" style={{ gridTemplateColumns: GRID_COLUMNS }}>
            {SORT_COLUMNS.map(({ col, label }) => (
              <div key={col}>{sortButton(col, label)}</div>
            ))}
            <div>Actions</div>
          </div>

          {units.length === 0 && (
            <div className="tosp-grid-empty">
              {buildings.length === 0
                ? "Add a building on the Buildings tab before creating units."
                : 'No units yet. Click "+ Add Unit" to create your first unit.'}
            </div>
          )}

          {sortRows(units).map((unit) => (
            <div key={unit.id} className="tosp-grid-row" style={{ gridTemplateColumns: GRID_COLUMNS }}>
              <div>{buildingName(unit.buildingId)}</div>
              <div>{unit.suiteNumber}</div>
              {/* Yellow rather than absent: an empty cell reads as a column
                  that failed to load, and a vacancy is the thing this tab is
                  scanned for. Same marking the Tax Rolls register uses. */}
              <div className="tosp-cell-wrap">
                {occupantName(unit.id) || (
                  <span className="tosp-roll-unassigned">None assigned</span>
                )}
              </div>
              <div>{unit.spaceType}</div>
              <div>{formatSqFt(unit.occupancySqFt)}</div>
              <div>{formatSqFt(unit.bmvaSqFt)}</div>
              <div>{formatCurrency(bmvaRate(configuration, unit))}</div>
              <div>{formatCurrency(bmvaAnnualFee(unit, configuration))}</div>
              <div>{formatCurrency(bmvaMonthlyFee(unit, configuration))}</div>
              <div className="tosp-actions">
                <button
                  className="tosp-btn-icon tosp-btn-view"
                  title="View"
                  onClick={() => setViewing(unit)}
                >
                  👁
                </button>
                <button
                  className="tosp-btn-icon tosp-btn-edit"
                  title="Edit"
                  onClick={() => setEditing(unit)}
                >
                  ✎
                </button>
                <button
                  className="tosp-btn-icon tosp-btn-delete"
                  title="Delete"
                  onClick={() => requestDelete(unit)}
                >
                  🗑
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <UnitModal
        isOpen={editing !== undefined}
        unit={editing ?? null}
        buildings={buildings}
        units={units}
        onClose={() => setEditing(undefined)}
        onSave={handleSave}
      />

      <UnitDetailsModal
        unit={viewing}
        buildingName={buildingName}
        onClose={() => setViewing(null)}
      />

      <InfoModal
        isOpen={!!blockedDelete}
        title="Unit In Use"
        message={blockedDelete || ""}
        onClose={() => setBlockedDelete(null)}
      />

      <DeleteConfirmModal
        isOpen={!!deleting}
        title="Delete Unit"
        message={
          deleting
            ? `Are you sure you want to delete Suite ${deleting.suiteNumber} in ${buildingName(
                deleting.buildingId,
              )}? This cannot be undone.`
            : ""
        }
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
      />
    </section>
  );
}
